/*
  File: src/components/Stream/IVSPlayer.jsx
  Purpose: Render Amazon IVS player, wire metadata cues to overlays and parent.
  Responsibilities:
  - Initialize IVS web player with low-latency option and recommended settings.
  - Load `playbackUrl` and forward TEXT_METADATA_CUE events to `onMetadata`.
  - Handle basic retry on errors and provide a container-level fullscreen toggle.
  Props:
  - playbackUrl: HLS URL to play
  - onMetadata(data): callback for parsed or extracted cue payloads
  - onPlayerReady(ref): callback with ref to underlying player instance
  Notes:
  - Preserves metadata even if cue text is malformed by extracting key tokens.
*/
import React, { useRef, useEffect, useCallback, useState } from 'react';
import WatchBottomMetadataOverlay from './WatchBottomMetadataOverlay';
import SeriesBottomMetadataOverlay from './SeriesBottomMetadataOverlay';
import RightSideMenu from './RightSideMenu';
import FootballMemoryGame from './FootballMemoryGame';
import BarHittingGame from './BarHittingGame';

export default function IVSPlayer({ playbackUrl, onMetadata, onPlayerReady, overlayMetadata, onLiveToggle, customLabelsConfig, matchId = null, isSeriesMode = false, sidebarVisible = true }) {
    const DEBUG_IVS = String(process.env.REACT_APP_DEBUG_IVS || '').toLowerCase() === 'true';
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const containerRef = useRef(null);
    const onMetadataRef = useRef(onMetadata);
    const [currentMetadata, setCurrentMetadata] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [isPlaying, setIsPlaying] = useState(true);
    const [favOrange, setFavOrange] = useState(false);
    const favOrangeRef = useRef(false);
    useEffect(() => { favOrangeRef.current = favOrange; }, [favOrange]);
    // Track whether the product panel is open to hide the top-left controller
    const [panelOpen, setPanelOpen] = useState(false);
    // Right menu state
    const [rightMenuOpen, setRightMenuOpen] = useState(false);
    // Track right menu width for progressive collapse
    const [rightMenuWidth, setRightMenuWidth] = useState(0);
    // In-player webview overlay (for betting providers or external content)
    const [webviewUrl, setWebviewUrl] = useState(null);
    const [webviewTitle, setWebviewTitle] = useState('');
    const [webviewStatus, setWebviewStatus] = useState('idle'); // idle | loading | loaded | failed
    const [webviewKey, setWebviewKey] = useState(0);
    const webviewTimerRef = useRef(null);
    // In-player mini-game overlay
    const [gameOpen, setGameOpen] = useState(false);
    const [selectedGame, setSelectedGame] = useState('memory'); // 'memory' or 'bar'
    const [showGameMenu, setShowGameMenu] = useState(false);

    // Compute dynamic right offset for top controls when side panels are open
    const controlsRightOffset = React.useMemo(() => {
        const menu = Math.max(0, Number(rightMenuWidth) || 0);
        const webv = webviewUrl ? (isFullscreen ? 360 : 300) : 0;
        return menu + webv + 8; // 8px margin
    }, [rightMenuWidth, webviewUrl, isFullscreen]);


    // Keep latest onMetadata callback
    useEffect(() => {
        onMetadataRef.current = onMetadata;
    }, [onMetadata]);

    // Close game menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showGameMenu && !event.target.closest('.game-menu-container')) {
                setShowGameMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showGameMenu]);

    // Handle fullscreen changes (track only)
    useEffect(() => {
        const handleFullscreenChange = () => {
            const fsEl = document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement;
            setIsFullscreen(!!fsEl);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    const enterContainerFullscreen = () => {
        const el = containerRef.current;
        if (!el) return;
        if (el.requestFullscreen) el.requestFullscreen();
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
        else if (el.msRequestFullscreen) el.msRequestFullscreen();
    };

    const exitFullscreen = () => {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
    };

    const toggleFullscreen = () => {
        const fsEl = document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement;
        if (fsEl) exitFullscreen();
        else enterContainerFullscreen();
    };

    const openInWebview = (url, title = '') => {
        // Keep the right menu open; place the webview to its left
        setWebviewTitle(String(title || ''));
        setWebviewUrl(String(url || ''));
        setWebviewStatus('loading');
        setWebviewKey((k) => k + 1);
        if (webviewTimerRef.current) clearTimeout(webviewTimerRef.current);
        webviewTimerRef.current = setTimeout(() => {
            setWebviewStatus((prev) => (prev === 'loading' ? 'failed' : prev));
        }, 3500);
    };

    const closeWebview = () => {
        if (webviewTimerRef.current) clearTimeout(webviewTimerRef.current);
        setWebviewUrl(null);
        setWebviewTitle('');
        setWebviewStatus('idle');
    };

    // Initialize IVS Player
    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl || !playbackUrl) return;

        // Check IVS Player support
        if (!window.IVSPlayer || !window.IVSPlayer.isPlayerSupported) {
            console.error('IVS Player not supported in this browser');
            return;
        }

        // Create player with AWS recommended settings
        const lowLatency = (process.env.REACT_APP_IVS_LOW_LATENCY || 'false').toLowerCase() === 'true';
        const player = window.IVSPlayer.create({
            wasmWorker: 'https://player.live-video.net/1.7.0/amazon-ivs-wasmworker.min.js',
            wasmBinary: 'https://player.live-video.net/1.7.0/amazon-ivs-wasmworker.min.wasm',
            lowLatencyMode: lowLatency,
        });

        // Attach to video element
        player.attachHTMLVideoElement(videoEl);
        player.setAutoplay(true);
        try { player.setMuted(true); setIsMuted(true); } catch (_) {}

        // Configure player for optimal performance
        player.setRebufferToLive(true); // Rebuffer to live edge
        player.setAutoQualityMode(true); // Enable adaptive bitrate

        // Load the stream
        player.load(playbackUrl);
        try { player.play(); setIsPlaying(true); } catch (_) { setIsPlaying(false); }

        // Safe extra play attempt shortly after load
        let playRetryTimer = null;
        playRetryTimer = setTimeout(() => {
            if (playerRef.current !== player) return; // player was replaced
            try { player.play(); } catch (_) {}
        }, 700);

        // Handle metadata cues
        const handleMetadata = (cue) => {
            if (DEBUG_IVS) console.log('IVSPlayer: Received metadata cue:', cue);
            const recvMs = Date.now();
            
            // Always print raw IVS metadata as received
            if (DEBUG_IVS) {
                try { console.log('IVS RAW METADATA:', typeof cue?.text === 'string' ? cue.text : cue); } catch (_e) {}
            }
            try {
                // Parse the JSON string from the cue
                const data = JSON.parse(cue.text);
                if (data && typeof data === 'object') {
                    data.tim = { ...(data.tim || {}), clt_recv_ms: recvMs };
                }
                if (DEBUG_IVS) console.log('IVSPlayer: Parsed metadata:', data);
                // Defer display to parent after enrichment
                onMetadataRef.current?.(data);
            } catch (err) {
                if (DEBUG_IVS) console.warn('Failed to parse metadata:', cue.text);
                // If JSON parsing fails, try to create a structured object from the raw text
                const rawText = cue.text;
                if (DEBUG_IVS) console.log('IVSPlayer: Raw metadata text:', rawText);

                if (rawText.includes('team') && rawText.includes('player')) {
                    // Try to extract data from malformed JSON - handle both quoted and unquoted formats
                    let teamMatch = rawText.match(/"team":"([^"]+)"/);
                    let playerMatch = rawText.match(/"player":"([^"]+)"/);
                    let numberMatch = rawText.match(/"number":(\d+)/);

                    // If no quoted matches, try unquoted format
                    if (!teamMatch) teamMatch = rawText.match(/team:([^,}]+)/);
                    if (!playerMatch) playerMatch = rawText.match(/player[\.:]([^,}]+)/);
                    if (!numberMatch) numberMatch = rawText.match(/number:(\d+)/);

                    if (teamMatch || playerMatch || numberMatch) {
                        const extractedData = {};
                        if (teamMatch) extractedData.team = teamMatch[1].trim();
                        if (playerMatch) extractedData.player = playerMatch[1].trim();
                        if (numberMatch) extractedData.number = parseInt(numberMatch[1]);
                        extractedData.tim = { clt_recv_ms: recvMs };

                        if (DEBUG_IVS) console.log('IVSPlayer: Extracted metadata from malformed JSON:', extractedData);
                        // Defer display to parent after enrichment
                        onMetadataRef.current?.(extractedData);
                    } else {
                        // Fallback to raw text
                        if (DEBUG_IVS) console.log('IVSPlayer: Could not extract structured data, using raw text');
                        const fallback = { raw: rawText, timestamp: recvMs, tim: { clt_recv_ms: recvMs } };
                        // Defer display to parent after enrichment
                        onMetadataRef.current?.(fallback);
                    }
                } else {
                    // Fallback to raw text
                    if (DEBUG_IVS) console.log('IVSPlayer: No team/player found, using raw text');
                    const fallback = { raw: rawText, timestamp: recvMs, tim: { clt_recv_ms: recvMs } };
                    // Defer display to parent after enrichment
                    onMetadataRef.current?.(fallback);
                }
            }
        };


        // Add event listeners
        player.addEventListener(
            window.IVSPlayer.PlayerEventType.TEXT_METADATA_CUE,
            handleMetadata
        );

        // Error handling with retry
        let retryCount = 0;
        const maxRetries = 3;

        const handleError = (err) => {
            if (DEBUG_IVS) console.warn('[IVS] Player error:', err);

            if (retryCount < maxRetries) {
                retryCount++;
                if (DEBUG_IVS) console.log(`[IVS] Retrying... (${retryCount}/${maxRetries})`);

                // Retry with cache-busted URL
                const retryUrl = `${playbackUrl}${playbackUrl.includes('?') ? '&' : '?'}retry=${retryCount}&t=${Date.now()}`;
                player.load(retryUrl);
            } else {
                if (DEBUG_IVS) console.error('[IVS] Max retries exceeded');
            }
        };

        if (window.IVSPlayer.PlayerEventType.ERROR) {
            player.addEventListener(window.IVSPlayer.PlayerEventType.ERROR, handleError);
        }
        if (window.IVSPlayer.PlayerEventType.STATE_CHANGED) {
            player.addEventListener(window.IVSPlayer.PlayerEventType.STATE_CHANGED, (state) => {
                try {
                    const s = state || player.getState?.();
                    const isPlay = String(s).toUpperCase() === 'PLAYING';
                    setIsPlaying(isPlay);
                } catch (_) {}
            });
        }

        // Watchdog: if not playing after a few seconds, reload with cache-busted URL once or twice
        let watchdogTimer = null;
        let reloadAttempts = 0;
        const kickIfStuck = () => {
            if (playerRef.current !== player) return; // replaced
            const nowPlaying = (() => {
                try { return String(player.getState?.()).toUpperCase() === 'PLAYING'; } catch (_) { return false; }
            })();
            if (nowPlaying) return;
            if (reloadAttempts >= 2) return;
            reloadAttempts += 1;
            try {
                const sep = playbackUrl.includes('?') ? '&' : '?';
                const retryUrl = `${playbackUrl}${sep}r=${Date.now()}&n=${reloadAttempts}`;
                if (DEBUG_IVS) console.log('[IVS] Watchdog reload attempt', reloadAttempts);
                player.load(retryUrl);
                try { player.play(); } catch (_) {}
            } catch (_) {}
            watchdogTimer = setTimeout(kickIfStuck, 2500);
        };
        watchdogTimer = setTimeout(kickIfStuck, 2500);

        // Store player reference
        playerRef.current = player;

        // Notify parent that player is ready
        if (onPlayerReady) {
            onPlayerReady(playerRef);
        }

        // Cleanup
        return () => {
            if (playRetryTimer) { try { clearTimeout(playRetryTimer); } catch (_) {} }
            if (watchdogTimer) { try { clearTimeout(watchdogTimer); } catch (_) {} }
            if (playerRef.current) {
                try { playerRef.current.pause(); } catch (_) {}
                try {
                    playerRef.current.removeEventListener(
                        window.IVSPlayer.PlayerEventType.TEXT_METADATA_CUE,
                        handleMetadata
                    );
                } catch (_) {}
                try {
                    if (window.IVSPlayer.PlayerEventType.ERROR) {
                        playerRef.current.removeEventListener(
                            window.IVSPlayer.PlayerEventType.ERROR,
                            handleError
                        );
                    }
                } catch (_) {}
                try {
                    if (window.IVSPlayer.PlayerEventType.STATE_CHANGED) {
                        // We passed an inline handler; safe to no-op removal
                        playerRef.current.removeEventListener(window.IVSPlayer.PlayerEventType.STATE_CHANGED, () => {});
                    }
                } catch (_) {}
                try { playerRef.current.delete(); } catch (_) {}
                playerRef.current = null;
            }
        };
    }, [playbackUrl, onPlayerReady]);

    const toggleMute = useCallback(() => {
        const p = playerRef.current;
        if (!p) return;
        const next = !isMuted;
        try { p.setMuted(next); } catch (_) {}
        try { if (videoRef.current) videoRef.current.muted = next; } catch (_) {}
        setIsMuted(next);
    }, [isMuted]);

    const togglePlay = useCallback(() => {
        const p = playerRef.current;
        if (!p) return;
        if (isPlaying) {
            try { p.pause(); } catch (_) {}
            setIsPlaying(false);
        } else {
            try { p.play(); setIsPlaying(true); } catch (_) {}
        }
    }, [isPlaying]);

    return (
        <>
            <div
                ref={containerRef}
                className="relative w-full aspect-video bg-black rounded-lg overflow-hidden group"
                id="video-container"
                style={{
                    paddingRight: (() => {
                        // Reduce embedded provider (webview) width so it doesn't take too much space
                        const webviewWidth = webviewUrl ? (isFullscreen ? 360 : 300) : 0;
                        const total = (rightMenuWidth || 0) + webviewWidth;
                        return total ? `${total}px` : undefined;
                    })(),
                    transition: 'padding-right 120ms ease-out',
                    willChange: 'padding-right'
                }}
            >
                <video
                    ref={videoRef}
                    playsInline
                    muted
                    autoPlay
                    // Disable native controls so clicks don't pause/mute
                    controls={false}
                    controlsList="nodownload noplaybackrate noplay"
                    crossOrigin="anonymous"
                    className="w-full h-full object-contain hide-native-fullscreen"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                />

                {/* Custom controls: only appear when hovering the top strip */}
                <div className={`absolute top-0 left-0 right-0 h-14 z-40 group ${panelOpen ? 'pointer-events-none' : ''}`}>
                    <div
                        className={`absolute top-2 flex items-center gap-2 transition-opacity ${panelOpen ? 'opacity-0 pointer-events-none' : (rightMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto')}`}
                        style={{ right: `${controlsRightOffset}px` }}
                    >
                        {!isSeriesMode && playbackUrl && (
                            <div className="relative game-menu-container">
                                <button
                                    type="button"
                                    onClick={() => setShowGameMenu(!showGameMenu)}
                                    className="bg-green-600/80 text-white h-8 px-2 rounded hover:bg-green-600 flex items-center justify-center"
                                    aria-label="Choose game to play"
                                    title="Play Games"
                                >
                                    <div className="flex items-center justify-center gap-1 leading-none select-none pointer-events-none whitespace-nowrap">
                                        <span className="text-[10px] leading-none">Play</span>
                                        <span className="text-[12px] leading-none" aria-hidden>🎮</span>
                                    </div>
                                </button>
                                {showGameMenu && (
                                    <div className="absolute top-full right-0 mt-1 bg-black/90 backdrop-blur-sm rounded-lg shadow-lg overflow-hidden z-50 min-w-40">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedGame('memory');
                                                setGameOpen(true);
                                                setShowGameMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-3 text-white hover:bg-emerald-600/50 flex items-center gap-3 transition-colors"
                                        >
                                            <span>🧠</span>
                                            <div>
                                                <div className="text-sm font-medium">Memory Game</div>
                                                <div className="text-xs text-gray-300">Match the cards</div>
                                            </div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedGame('bar');
                                                setGameOpen(true);
                                                setShowGameMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-3 text-white hover:bg-orange-600/50 flex items-center gap-3 transition-colors"
                                        >
                                            <span>⚽</span>
                                            <div>
                                                <div className="text-sm font-medium">Bar Hit Game</div>
                                                <div className="text-xs text-gray-300">Hit the crossbar</div>
                                            </div>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={togglePlay}
                            className="bg-black/60 text-white w-8 h-8 rounded hover:bg-black/80 flex items-center justify-center"
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                                    <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden="true">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={toggleMute}
                            className="bg-black/60 text-white w-8 h-8 rounded hover:bg-black/80 flex items-center justify-center"
                            aria-label={isMuted ? 'Unmute' : 'Mute'}
                        >
                            {isMuted ? (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                                    <path d="M16.5 12c0-1.77-.77-3.35-2-4.44v8.88c1.23-1.09 2-2.67 2-4.44z" opacity=".3"/>
                                    <path d="M3 10v4h4l5 5V5L7 10H3zm13.59 2L21 6.59 19.59 5 16 8.59 12.41 5 11 6.41 14.59 10 11 13.59 12.41 15 16 11.41 19.59 15 21 13.59 16.59 9z" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                                    <path d="M3 10v4h4l5 5V5L7 10H3z" />
                                    <path d="M16.5 12c0-1.77-.77-3.35-2-4.44v8.88c1.23-1.09 2-2.67 2-4.44z" />
                                    <path d="M14.5 3.23v2.06c2.89 1 5 3.77 5 6.71s-2.11 5.71-5 6.71v2.06c4.01-1.14 7-4.93 7-8.77s-2.99-7.63-7-8.77z" />
                                </svg>
                            )}
                        </button>
                        {/* Right menu toggle (second from right, next to fullscreen) */}
                        {sidebarVisible && (
                            <button
                                type="button"
                                onClick={() => setRightMenuOpen(v => !v)}
                                className="bg-black/60 text-white w-8 h-8 rounded hover:bg-black/80 flex items-center justify-center"
                                aria-label={rightMenuOpen ? 'Hide menu' : 'Show menu'}
                                title={rightMenuOpen ? 'Hide info menu' : 'Show info menu'}
                            >
                                {/* Simple menu icon */}
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                                    <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
                                </svg>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={toggleFullscreen}
                            className="bg-black/60 text-white w-8 h-8 rounded hover:bg-black/80 flex items-center justify-center"
                            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                        >
                            {isFullscreen ? (
                                // Exit fullscreen icon (compress)
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                                    <path d="M16 14h2v4h-4v-2h2v-2zm-6 4H6v-4h2v2h2v2zM16 6v2h2v2h2V6h-4zm-6 0H6v4h2V8h2V6z" />
                                </svg>
                            ) : (
                                // Enter fullscreen icon (expand)
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                                    <path d="M7 14H5v5h5v-2H7v-3zm12 5h-5v-2h3v-3h2v5zM7 5h3V3H5v5h2V5zm12 0v3h-3v2h5V3h-2z" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                {/* Top-left SceneShop logo toggle (always visible) */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setFavOrange((v) => {
                            const next = !v;
                            try { onLiveToggle && onLiveToggle(!next); } catch (_) {}
                            return next;
                        });
                    }}
                    className="absolute top-0 left-0 z-50 cursor-pointer"
                    aria-label={favOrange ? 'Hide overlay (off)' : 'Show overlay (on)'}
                    title={favOrange ? 'Overlay off' : 'Overlay on'}
                    style={{ lineHeight: 0 }}
                >
                    {(() => {
                        const enabled = !favOrange;
                        const src = enabled ? '/sceneshop-solid.svg' : '/sceneshop-outline.svg';
                        const logoSize = isFullscreen ? 80 : 64; // doubled sizes for fullscreen and normal
                        return (
                            <img
                                src={src}
                                alt={enabled ? 'SceneShop on' : 'SceneShop off'}
                                width={logoSize}
                                height={logoSize}
                                style={{ display: 'block' }}
                                onError={(e) => { e.currentTarget.src = '/favicon.ico'; }}
                            />
                        );
                    })()}
                </button>

                {/* Right slide-in menu (Stats/Events/Betting) */}
                {sidebarVisible && (
                    <RightSideMenu
                        open={rightMenuOpen}
                        onClose={() => setRightMenuOpen(false)}
                        isFullscreen={isFullscreen}
                        matchId={matchId}
                        onWidthChange={(w) => setRightMenuWidth(Math.max(0, Number(w) || 0))}
                        onOpenWebview={!isSeriesMode ? (url, provider) => openInWebview(url, provider?.name || 'External') : null}
                    />
                )}

                {/* Bottom Metadata Overlay fed by parent (post-enrichment).
                    Keep mounted even when hidden so it continues ingesting detections. */}
                <div className={`absolute left-0 right-0 bottom-0 z-30 ${favOrange ? 'opacity-0 pointer-events-none' : ''}`}>
                    {isSeriesMode ? (
                        <SeriesBottomMetadataOverlay
                            metadata={overlayMetadata || null}
                            isFullscreen={isFullscreen}
                            customLabelsConfig={customLabelsConfig}
                            onPanelVisibilityChange={(open) => setPanelOpen(!!open)}
                        />
                    ) : (
                        <WatchBottomMetadataOverlay
                            metadata={overlayMetadata || null}
                            isFullscreen={isFullscreen}
                            customLabelsConfig={customLabelsConfig}
                            onPanelVisibilityChange={(open) => {
                                setPanelOpen(!!open);
                                // Close right menu when product panel opens
                                if (open) setRightMenuOpen(false);
                            }}
                            sidebarOpen={sidebarVisible}
                            rightMenuWidth={rightMenuWidth}
                        />
                    )}
                </div>
                {/* Webview side panel: appears to the left of the right menu */}
                {webviewUrl && (
                    <div
                        className="absolute top-0 z-40 flex flex-col bg-black/80 border-l border-white/10"
                        style={{ 
                            right: `${rightMenuWidth}px`, 
                            width: isFullscreen ? 360 : 300,
                            bottom: `${isFullscreen ? 102 : 88}px`
                        }}
                    >
                        <div className="h-10 sm:h-12 flex items-center justify-between px-3 sm:px-4 bg-black/70 border-b border-white/10">
                            <div className="text-xs sm:text-sm text-white/80 truncate">{webviewTitle || 'External Content'}</div>
                            <div className="flex items-center gap-2">
                                <a href={webviewUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] sm:text-xs px-2 py-1 rounded border border-white/20 text-white/80 hover:bg-white/10">New tab</a>
                                <button type="button" onClick={closeWebview} className="text-[11px] sm:text-xs px-2 py-1 rounded border border-white/20 text-white bg-white/10 hover:bg-white/20">Close</button>
                            </div>
                        </div>
                        <div className="relative flex-1 w-full h-full">
                            <iframe
                                key={webviewKey}
                                title={webviewTitle || 'webview'}
                                src={webviewUrl}
                                className="absolute inset-0 w-full h-full bg-white"
                                allow="autoplay; clipboard-read; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                sandbox="allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
                                onLoad={() => {
                                    if (webviewTimerRef.current) clearTimeout(webviewTimerRef.current);
                                    setWebviewStatus('loaded');
                                }}
                                onError={() => {
                                    if (webviewTimerRef.current) clearTimeout(webviewTimerRef.current);
                                    setWebviewStatus('failed');
                                }}
                            />
                            {webviewStatus === 'loading' && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="animate-spin inline-block w-8 h-8 border-4 border-white/30 border-t-white rounded-full" />
                                        <div className="text-xs text-white/80">Loading provider…</div>
                                    </div>
                                </div>
                            )}
                            {webviewStatus === 'failed' && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                                    <div className="text-center px-4">
                                        <div className="text-sm text-white/90 mb-2">This provider cannot be embedded here.</div>
                                        <div className="text-xs text-white/70 mb-3">Some sites block loading inside other apps. You can open it in a new tab.</div>
                                        <div className="flex items-center justify-center gap-2">
                                            <a href={webviewUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded-full bg-white text-black text-sm font-semibold hover:bg-slate-100">Open in new tab</a>
                                            <button
                                                type="button"
                                                className="px-3 py-2 rounded-full border border-white/30 text-white/90 text-sm hover:bg-white/10"
                                                onClick={() => {
                                                    setWebviewStatus('loading');
                                                    setWebviewKey((k) => k + 1);
                                                    if (webviewTimerRef.current) clearTimeout(webviewTimerRef.current);
                                                    webviewTimerRef.current = setTimeout(() => {
                                                        setWebviewStatus((prev) => (prev === 'loading' ? 'failed' : prev));
                                                    }, 3500);
                                                }}
                                            >Try again</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {/* Mini Games overlay */}
                {!isSeriesMode && gameOpen && (
                    <>
                        {selectedGame === 'memory' && (
                            <FootballMemoryGame open={gameOpen} onClose={() => setGameOpen(false)} />
                        )}
                        {selectedGame === 'bar' && (
                            <BarHittingGame open={gameOpen} onClose={() => setGameOpen(false)} />
                        )}
                    </>
                )}
            </div>
        </>
    );
}
